import { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import useLocalStorage from './useLocalStorage';

// ----------------------------------------------------------------------

export interface UseCollapsiblePanelResult {
  /** Whether the panel's body should currently be rendered expanded. */
  isExpanded: boolean;
  /** Flip the persisted expanded/collapsed preference. No-op effect on desktop (always expanded there). */
  toggle: () => void;
  /** True only below the `md` breakpoint - mirrors "is this the mobile layout". */
  isCollapsible: boolean;
}

/**
 * Drives mobile-only collapsible sections (e.g. Dashboard "Quick Filters" / "Recent Activity").
 *
 * - On desktop (`>= md`) the panel is ALWAYS expanded, regardless of any stored preference.
 * - On mobile (`< md`) the panel's expanded state is persisted per-user in localStorage under
 *   `storageKey`, defaulting to collapsed (`false`) the first time there's no stored value.
 * - Crossing the breakpoint (resize/rotate) is reflected immediately because `isExpanded` is derived
 *   on every render from the live `useMediaQuery` result - there is no stale local state to reconcile.
 */
export const useCollapsiblePanel = (storageKey: string): UseCollapsiblePanelResult => {
  const isCollapsible = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [storedExpanded, setStoredExpanded] = useLocalStorage<boolean>(storageKey, false);

  const isExpanded = useMemo(() => (!isCollapsible ? true : storedExpanded), [isCollapsible, storedExpanded]);

  const toggle = () => {
    setStoredExpanded((current: boolean) => !current);
  };

  return { isExpanded, toggle, isCollapsible };
};

export default useCollapsiblePanel;
